const ESG_PURCHASED_ELECTRICITY = "Purchased Electricity";

const normalizeLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ");

const siteBelongsToLegalEntity = (site, entityName) => {
  if (!site || !entityName) return false;
  const map = site.legalEntityMapping;
  if (!map || typeof map !== "object") return false;
  const target = normalizeLabel(entityName);
  return Object.keys(map).some((key) => normalizeLabel(key) === target);
};

const parseRecordDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  const str = String(value).trim();
  const ddmmyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const match = str.match(ddmmyyyy);
  if (match) {
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const year = parseInt(match[3], 10);
    const date = new Date(year, month, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const iso = new Date(str);
  return Number.isNaN(iso.getTime()) ? null : iso;
};

const formatDateKey = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const findField = (record, matcher) => {
  if (!record || typeof record !== "object") return null;
  const key = Object.keys(record).find((k) => matcher(k.toLowerCase()));
  return key != null && record[key] != null && record[key] !== "" ? record[key] : null;
};

const parseConsumptionMwh = (value) => {
  const num = Number.parseFloat(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(num) ? num : 0;
};

const isRenewableEnergySource = (energySource) => {
  const normalized = normalizeLabel(energySource);
  return normalized === "re" || normalized === "renewable" || normalized.includes("renewable");
};

/** Allocation % from site legalEntityMapping for one reporting legal entity (0–100). */
export const getLegalEntityAllocationPercent = (site, legalEntityName) => {
  const map = site?.legalEntityMapping;
  if (!map || typeof map !== "object") return 0;
  const target = normalizeLabel(legalEntityName);
  const entry = Object.entries(map).find(([key]) => normalizeLabel(key) === target);
  if (!entry) return 0;
  let pct = Number.parseFloat(String(entry[1]).replace("%", "").trim());
  if (!Number.isFinite(pct)) return 0;
  // Some APIs store 0.5 instead of 50
  if (pct > 0 && pct <= 1) pct *= 100;
  return pct;
};

/**
 * Whether this site's bills should count toward the reporting legal entity(ies).
 * Report history stores full site consumption per site location; legal-entity %
 * only controls which sites are included, not a multiplier on the bill value.
 */
export const getLegalEntityConsumptionFactor = (site, legalEntities) => {
  if (!Array.isArray(legalEntities) || !legalEntities.length) return 0;

  let totalPercent = 0;
  for (const entityName of legalEntities) {
    const name = entityName?.name || entityName;
    if (!name || !siteBelongsToLegalEntity(site, name)) continue;
    totalPercent += getLegalEntityAllocationPercent(site, name);
  }

  return totalPercent > 0 ? 1 : 0;
};

const normalizeReportRecord = (raw, siteLocation) => {
  const nested = raw?.energyReportRequest;

  const startRaw = findField(raw, (k) => k.includes("start") && k.includes("date")) ?? nested?.startDate ?? nested?.StartDate;
  const endRaw = findField(raw, (k) => k.includes("end") && k.includes("date")) ?? nested?.endDate ?? nested?.EndDate;
  const energySource = findField(raw, (k) => k.includes("energy") && k.includes("source")) ?? nested?.energySource ?? nested?.EnergySource;
  const consumptionRaw = findField(raw, (k) => k.includes("electricity") && k.includes("consumption")) ?? nested?.electricityConsumption ?? nested?.ElectricityConsumption;

  const periodStart = parseRecordDate(startRaw);
  const periodEnd = parseRecordDate(endRaw);
  if (!periodStart || !periodEnd) return null;

  const site = findField(raw, (k) => k.includes("site") && k.includes("location")) ?? nested?.siteLocation ?? siteLocation;

  return {
    siteLocation: String(site || siteLocation || "").trim(),
    periodStart,
    periodEnd,
    periodStartKey: formatDateKey(periodStart),
    periodEndKey: formatDateKey(periodEnd),
    energySource: String(energySource || "").trim(),
    isRenewable: isRenewableEnergySource(energySource),
    consumptionMwh: parseConsumptionMwh(consumptionRaw),
  };
};

/**
 * Deduplicate billing periods per site.
 * - Prefer RE over Non-RE when both exist for the same period.
 * - Keep only one Non-RE record when duplicates exist.
 */
export const deduplicateBillingPeriodRecords = (records) => {
  if (!Array.isArray(records) || !records.length) return [];

  const groups = new Map();
  for (const record of records) {
    const key = `${normalizeLabel(record.siteLocation)}|${record.periodStartKey}|${record.periodEndKey}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(record);
  }

  const deduped = [];
  for (const group of groups.values()) {
    const renewableRecords = group.filter((r) => r.isRenewable);
    deduped.push(renewableRecords.length ? renewableRecords[0] : group[0]);
  }
  return deduped;
};

/** Indian FY window: Apr(startYear) through Mar(startYear + 1). Uses billing period start date. */
export const billingPeriodInFinancialYear = (periodStart, fyStartYear) => {
  if (!periodStart || !Number.isFinite(fyStartYear)) return false;
  const year = periodStart.getFullYear();
  const month = periodStart.getMonth();
  return (year === fyStartYear && month >= 3) || (year === fyStartYear + 1 && month <= 2);
};

export const sumElectricityByFinancialYear = (records, fyStartYear) => {
  const totals = { renewableMwh: 0, nonRenewableMwh: 0, totalMwh: 0 };

  for (const record of records) {
    if (!billingPeriodInFinancialYear(record.periodStart, fyStartYear)) continue;
    const mwh = record.consumptionMwh;
    totals.totalMwh += mwh;
    if (record.isRenewable) totals.renewableMwh += mwh;
    else totals.nonRenewableMwh += mwh;
  }

  return totals;
};

const fetchSiteLocations = async (username) => {
  const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}list-sitelocation`, {
    method: "GET",
    headers: { username },
  });
  if (response.status !== 200) return [];
  const json = await response.json();
  if (json.success === false || json.status === 400) return [];
  return Array.isArray(json.data) ? json.data.filter((site) => site.isActive !== false) : [];
};

const fetchReportHistoryForSite = async (username, siteLocation) => {
  const headers = new Headers();
  headers.append("username", username);
  headers.append("sl", siteLocation);
  headers.append("esg", ESG_PURCHASED_ELECTRICITY);

  const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}report-history-dynamic`, {
    method: "GET",
    headers,
  });

  if (response.status !== 200) return [];
  const json = await response.json();
  if (json.success === false || json.status === 400) return [];
  return Array.isArray(json.data?.records) ? json.data.records : Array.isArray(json.data) ? json.data : [];
};

/**
 * Fetch purchased-electricity report history for BRSR Principle 6.
 * Applies site filtering by legal entity, billing-period deduplication, and RE priority.
 */
export const fetchBrsrElectricityByFy = async ({ username, legalEntities, fyStartYear }) => {
  const empty = { renewableMwh: 0, nonRenewableMwh: 0, totalMwh: 0 };
  if (!username || !Array.isArray(legalEntities) || !legalEntities.length || !Number.isFinite(fyStartYear)) {
    return { current: { ...empty }, previous: { ...empty } };
  }

  const entityNames = legalEntities.map((entity) => entity?.name || entity).filter(Boolean);
  const sites = await fetchSiteLocations(username);
  const relevantSites = sites.filter((site) => entityNames.some((name) => siteBelongsToLegalEntity(site, name)));

  const normalizedRecords = [];
  for (const site of relevantSites) {
    const siteName = site.name;
    if (!siteName) continue;

    const consumptionFactor = getLegalEntityConsumptionFactor(site, entityNames);
    if (consumptionFactor <= 0) continue;

    const rawRecords = await fetchReportHistoryForSite(username, siteName);

    for (const raw of rawRecords) {
      const normalized = normalizeReportRecord(raw, siteName);
      if (!normalized) continue;
      normalized.consumptionMwh *= consumptionFactor;
      normalizedRecords.push(normalized);
    }
  }

  // Deduplicate billing periods (RE over Non-RE) after scaling, before FY totals.
  const dedupedRecords = deduplicateBillingPeriodRecords(normalizedRecords);

  return {
    current: sumElectricityByFinancialYear(dedupedRecords, fyStartYear),
    previous: sumElectricityByFinancialYear(dedupedRecords, fyStartYear - 1),
  };
};
